import capitalize from 'lodash/capitalize';
import {
  BookOpen,
  Brain,
  Building2,
  CalendarClock,
  KeyRound,
  Landmark,
  Lightbulb,
  LogOut,
  Monitor,
  Moon,
  ShieldCheck,
  Sun,
  UserRound
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth, useConfig } from '@chainlit/react-client';

import { useTheme } from '@/components/ThemeProvider';
import { apiAdminMe } from '@/components/admin/api';
import { apiFinancialSettings } from '@/components/financial/api';
import { fetchProfilePending } from '@/components/know-your-data/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { OPEN_REPORTS_EVENT } from '@/components/welcome/api';
import { Translator } from 'components/i18n';

import { useFeatures } from '@/hooks/useFeatures';

/**
 * The avatar menu: everything that used to be an icon in the header and is
 * not a section of the product — scheduled reports, financial settings,
 * Configurations, API keys, the readme, the theme, and logging out.
 *
 * Each entry appears only where it used to: the same feature flag, the same
 * server answer (admin, chart of accounts), the same config. Financial
 * settings kept a header slot because its badge counts questions someone has
 * to answer; that count now shows on the menu entry and as a dot on the
 * avatar, so it is still seen without opening anything.
 */
export default function AccountMenu() {
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const { features, loading } = useFeatures();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [financial, setFinancial] = useState<{ pending: number } | null>(null);
  // Know your data + Business profile: every company user has them. The
  // server answers the badge for them and refuses admins, so the answer is
  // also what decides whether the two entries show.
  const [profile, setProfile] = useState<{ pending: number } | null>(null);
  const kydOn = profile !== null;
  const profilePending = profile?.pending ?? 0;

  useEffect(() => {
    let cancelled = false;
    apiAdminMe()
      .then(() => !cancelled && setIsAdmin(true))
      .catch(() => !cancelled && setIsAdmin(false));
    apiFinancialSettings()
      .then((s) => {
        if (cancelled) return;
        setFinancial(s.available ? { pending: s.questions.length } : null);
      })
      .catch(() => !cancelled && setFinancial(null));
    return () => {
      cancelled = true;
    };
  }, []);

  // Unanswered business-profile questions (the menu badge).
  useEffect(() => {
    let cancelled = false;
    fetchProfilePending()
      .then((r) => !cancelled && setProfile({ pending: r.pending }))
      .catch(() => !cancelled && setProfile(null));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!user) return null;
  const displayName = user?.display_name || user?.identifier;
  const reportsOn = !loading && !!features.scheduled_reports;
  const memoryOn = !loading && !!features.tenant_memory;
  const hasApiKeys = !!config?.userEnv?.length;
  const hasReadme = !!config?.markdown;
  const pending = financial?.pending ?? 0;
  const dot = pending + (kydOn ? profilePending : 0);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            id="user-nav-button"
            variant="ghost"
            className="relative h-8 w-8 rounded-full"
            aria-label="Account menu"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.metadata.image} alt="user image" />
              <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                {capitalize(displayName[0])}
              </AvatarFallback>
            </Avatar>
            {dot > 0 ? (
              <span
                className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background"
                style={{ background: 'hsl(var(--destructive))' }}
                aria-label={`${dot} question${dot === 1 ? '' : 's'} waiting`}
              />
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-60" align="end">
          <DropdownMenuLabel className="font-normal">
            <p className="truncate text-sm font-medium leading-none">
              {displayName}
            </p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={() => navigate('/profile')}>
            <UserRound className="size-4 text-muted-foreground" />
            My profile
          </DropdownMenuItem>
          {reportsOn ? (
            <DropdownMenuItem
              onClick={() =>
                window.dispatchEvent(new Event(OPEN_REPORTS_EVENT))
              }
            >
              <CalendarClock className="size-4 text-muted-foreground" />
              Scheduled reports
            </DropdownMenuItem>
          ) : null}
          {memoryOn ? (
            <DropdownMenuItem onClick={() => navigate('/memory')}>
              <Brain className="size-4 text-muted-foreground" />
              Signals memory
            </DropdownMenuItem>
          ) : null}
          {kydOn ? (
            <DropdownMenuItem onClick={() => navigate('/know-your-data')}>
              <Lightbulb className="size-4 text-muted-foreground" />
              Know your data
            </DropdownMenuItem>
          ) : null}
          {kydOn ? (
            <DropdownMenuItem onClick={() => navigate('/business-profile')}>
              <Building2 className="size-4 text-muted-foreground" />
              Business profile
              {profilePending > 0 ? (
                <span
                  className="ml-auto rounded-full px-1.5 text-[10px] font-bold leading-4 text-white"
                  style={{ background: 'hsl(var(--destructive))' }}
                >
                  {profilePending}
                </span>
              ) : null}
            </DropdownMenuItem>
          ) : null}
          {financial ? (
            <DropdownMenuItem onClick={() => navigate('/financial-settings')}>
              <Landmark className="size-4 text-muted-foreground" />
              Financial settings
              {pending > 0 ? (
                <span
                  className="ml-auto rounded-full px-1.5 text-[10px] font-bold leading-4 text-white"
                  style={{ background: 'hsl(var(--destructive))' }}
                >
                  {pending}
                </span>
              ) : null}
            </DropdownMenuItem>
          ) : null}
          {isAdmin ? (
            <DropdownMenuItem onClick={() => navigate('/admin')}>
              <ShieldCheck className="size-4 text-muted-foreground" />
              Configurations
            </DropdownMenuItem>
          ) : null}
          {hasApiKeys ? (
            <DropdownMenuItem onClick={() => navigate('/env')}>
              <KeyRound className="size-4 text-muted-foreground" />
              <Translator path="navigation.user.menu.apiKeys" />
            </DropdownMenuItem>
          ) : null}
          {hasReadme ? (
            <DropdownMenuItem onClick={() => navigate('/help')}>
              <BookOpen className="size-4 text-muted-foreground" />
              <Translator path="navigation.header.readme" />
            </DropdownMenuItem>
          ) : null}

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Sun className="size-4 text-muted-foreground dark:hidden" />
              <Moon className="hidden size-4 text-muted-foreground dark:block" />
              Theme
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup
                value={theme || 'system'}
                onValueChange={(v) =>
                  setTheme(v as 'light' | 'dark' | 'system')
                }
              >
                <DropdownMenuRadioItem value="light">
                  <Sun className="mr-2 size-4" />
                  <Translator path="navigation.header.theme.light" />
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="dark">
                  <Moon className="mr-2 size-4" />
                  <Translator path="navigation.header.theme.dark" />
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="system">
                  <Monitor className="mr-2 size-4" />
                  <Translator path="navigation.header.theme.system" />
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => logout(true)}>
            <LogOut className="size-4 text-muted-foreground" />
            <Translator path="navigation.user.menu.logout" />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
